<template>
  <div class="counter-item border-l border-l-accent pl-6 lg:pl-8 flex flex-col gap-2">
    <div class="h6 uppercase">{{ item.counterItemUpperText }}</div>
    <p class="text-[80px] text-accent font-bold leading-[1.3]">{{ number.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) }}</p>
    <div class="h6 uppercase">{{ item.counterItemSubText }}</div>
  </div>
</template>

<script lang="ts" setup>
  import type { GraphQLCounterItem } from '~/types/graphql';
  import type { SalesStatisticsQueryResult } from '~/types/api';
  const { locale } = useI18n();

  const props = withDefaults(
    defineProps<{
      item: GraphQLCounterItem;
      salesStatistics?: SalesStatisticsQueryResult;
      isVisible: boolean;
      index: number;
    }>(),
    {
      isVisible: false,
    }
  );

  const number = ref(props.item.counterItemStartFrom || 0);

  const animating = ref(false);
  const completed = ref(false);

  // Easing function for smooth animation (ease-out cubic)
  const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  };

  watch(
    () => props.isVisible,
    visible => {
      if (!visible) return;
      if (animating.value) return;
      if (completed.value) return;

      animating.value = true;
      const startValue = props.item.counterItemStartFrom || 0;
      let endValue = props.item.counterItemNumber;

      if (props.item.counterItemUseStatistic) {
        if (props.item.counterItemUseStatistic === 'total_sales') {
          endValue = props.salesStatistics?.total_sales || 0;
        }
      }

      const duration = props.item.counterItemCounterTimerSec * 1000;
      const startTime = Date.now();

      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Apply easing
        const easedProgress = easeOutCubic(progress);

        // Calculate current value
        number.value = Math.round(startValue + (endValue - startValue) * easedProgress);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          number.value = endValue;
          animating.value = false;
          completed.value = true;
        }
      };

      requestAnimationFrame(animate);
    }
  );
</script>

<style></style>
